import type { MessageOrProblem } from "@shared/backend-api.types";
import type { NotificationInsertSupa, NotificationSupa } from "@shared/supabase.types.ts";
import { supabaseClient } from "@/lib/supabase";
import { createAnalytic } from "@/services/entities/use-analytics";
import {
  createNotification as createNotificationUtil,
  fetchAllNotifications,
} from "@/services/entities/use-notifications";
import { callBackend } from "./api-utilities";

import { getCurrentUserId, isClient, isTutor } from "./auth-utilities";

// import { ADMIN_ALERT_EMAIL, ADMIN_ALERT_PHONE } from "./constants";

type Urgency = 1 | 2 | 3; // 1 is default, 2 is urgent, and 3 is extremely urgent (TODO cause different SMS and email based on urgency)

/**
 * // TODO defined better when activities should be urgency 1, 2, or 3. also allow for componenets to override the urgency
 * A method to create a new notification model instance. If sending a notification to an Admin, you cannot specify a recipientId because there is
 * no Model Table named Admin. All admins will get the same instnace of the notification.
 * @param title The title of the notification
 * @param message the Message of the notification
 * @param urgency Int. 1 for standard, 2 for urgent, and 3 for extremely urgent notifications. 2 will attempt send an SMS to the recipient, and 3 will attempt send an SMS and an email
 
 * @returns void since it returns immediately and runs as a basckground task
 */
export async function createNotification(
  title: string,
  message: string,
  urgency: Urgency,
  staffId?: string,
  userId?: string,
  sendEmailOrText: boolean | undefined = true,
): Promise<void> {
  const phoneNumbers: string[] = [];
  const emails: string[] = [];
  // return a resolved promise to immediately stop other functions on waiting for this function to finish
  const returnPromise = Promise.resolve();

  // Checks on the parameters
  if (title === "" && message === "") {
    console.error("Title and Message cannot both be empty strings when creating a notification");
    await createAnalytic({
      class: "ERROR",
      event: {
        event: "createNotification",
        error: "Title and Message cannot both be empty strings when creating a notification",
        title: title,
        message: message,
        urgency: urgency,
        staffId,
        userId,
      },
    });
    return returnPromise;
  }
  if ([1, 2, 3].indexOf(urgency) === -1) {
    console.error(`Urgency must be 1, 2, or 3. Received ${urgency}`);
    await createAnalytic({
      class: "ERROR",
      event: {
        event: "createNotification",
        error: "Urgency must be 1, 2, or 3",
        title: title,
        message: message,
        urgency: urgency,
        staffId,
        userId,
      },
    });
    return returnPromise;
  }

  // gather the phone numbers of the recipients
  if (urgency > 1 && sendEmailOrText) {
  } else if (staffId && sendEmailOrText) {
    const res = await supabaseClient
      .from("staff")
      .select("phone")
      .eq("staff_id", staffId)
      .maybeSingle();
    if (res.data?.phone) {
      phoneNumbers.push(res.data.phone);
    }
  }

  // gather the emails of the recipients
  if (urgency === 3 && sendEmailOrText) {
  } else if (staffId && sendEmailOrText) {
    const res = await supabaseClient
      .from("staff")
      .select("email")
      .eq("staff_id", staffId)
      .maybeSingle();
    if (res.data?.email) {
      emails.push(res.data.email);
    }
  }

  // send the SMS in for loop and keep a log of the results
  if (phoneNumbers.length > 0) {
    for (let i = 0; i < phoneNumbers.length; i++) {
      const phoneNumber = phoneNumbers[i];
      await sendText(phoneNumber, message);
    }
  }

  // send the emails in for loop and keep a log of the results
  if (emails.length > 0) {
    for (let i = 0; i < emails.length; i++) {
      const email = emails[i];
      await sendEmail(email, title, message);
    }
  }

  const notificationInput: NotificationInsertSupa = {
    title: title,
    message: message,
    staff_id: staffId,
    user_id: userId,
    is_read: false,
    urgency: urgency,
  };
  try {
    await createNotificationUtil(notificationInput);
    return returnPromise;
  } catch (error) {
    console.error("Error creating notification");
    await createAnalytic({
      class: "ERROR",
      event: {
        event: "createNotification",
        error: "Error creating notification",
        title: title,
        message: message,
        urgency: urgency,
        staffId,
        userId,
      },
    });
  }
}

/**
 * Gets the notifications that are not read belonging to the current Signed In user
 * If the current signed in user is not a tutor, the function will return null
 * @param is_read boolean. If true, will return read notifications. If false, will return unread notifications
 */
export async function getTutorNotifications(
  staffId: string,
  is_read = false,
): Promise<NotificationSupa[]> {
  const notifications = await fetchAllNotifications(staffId, undefined);
  return notifications.filter((notification) => notification.is_read === is_read);
}

/**
 * Gets the notifications that are not read belonging to the current Signed In user
 * If the current signed in user is not a client, the function will return null
 * @param is_read boolean. If true, will return read notifications. If false, will return unread notifications
 */
export async function getClientNotifications(
  userId: string,
  is_read = false,
): Promise<NotificationSupa[]> {
  const notifications = await fetchAllNotifications(undefined, userId);
  return notifications.filter((notification) => notification.is_read === is_read);
}

/**
 * Gets the notifications that are not read belonging to the current Signed In user
 * @param is_read if true, will return read notifications
 */
export async function getAllNotifications(
  // TODO needs to be better thought, or else it results in a lot of graphql calls
  is_read = false,
): Promise<NotificationSupa[]> {
  let n: NotificationSupa[] = [];
  if (await isTutor()) {
    const sub = await getCurrentUserId();
    if (sub) {
      const tutor = await getTutorNotifications(sub, is_read);
      n = n.concat(tutor);
    }
  } else if (await isClient()) {
    const sub = await getCurrentUserId();
    if (sub) {
      const client = await getClientNotifications(sub, is_read);
      n = n.concat(client);
    }
  }
  return n;
}

// TODO this is an open API right now, need to add authentication
export async function sendEmail(
  email: string,
  subject: string,
  message: string,
): Promise<MessageOrProblem> {
  return await callBackend("/notifications/email", "POST", {
    message: message,
    email: email,
    subject: subject,
  });
}

export async function sendText(phoneNumber: string, message: string): Promise<MessageOrProblem> {
  return await callBackend("/notifications/text", "POST", {
    message: message,
    phoneNumber: phoneNumber,
  });
}
